// @flow
import * as React from 'react';
import {useState} from 'react';

import Group from 'sail/Group';
import Box from 'sail/Box';
import {Body, BodyAlt} from 'sail/Text';
import Button from 'sail/Button';
import {TextInput} from 'sail/Input';
import Checkbox from 'sail/Checkbox';
import {InfoTooltip} from 'sail/Tooltip';

import Dialog, {DialogHeader, DialogContent, DialogFooter} from 'sail/Dialog';
import PropertyList, {PropertyListItem} from 'sail/PropertyList';
import {useDiagramGenerator} from 'docs/data/diagram';
import {generateId} from '../util/generateId';
import {SetNodeId} from './SetNodeId';

export const AddEntityNode = () => {
  const [id, setId] = useState('');
  const [type, setType] = useState('');
  const [attributes, setAttributes] = useState([]);
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [hasHandle, setHasHandle] = useState(false);
  const {addNode} = useDiagramGenerator();

  const add = () => {
    // We have to assemble the Markdoc AST since
    // we don't have a way to parse raw Markdoc text in the browser
    const table = {
      children: [null],
    };
    const handles = [];
    const attrs = attributes.map((a) => {
      let attrVal = a.value;
      if (a.hasHandle) {
        // If this row has a handle to draw an edge to another node
        // we need to add a nested node around the value
        attrVal = {
          name: 'node',
          attributes: {
            id: a.name,
          },
          children: [a.value],
        };
        handles.push(a.name);
      }
      return {
        children: [
          {children: [a.name]},
          {children: [attrVal]},
          a.hasHandle ? {children: [a.name]} : null,
        ],
      };
    });
    table.children.push({children: attrs});
    addNode({id, handles}, [type, table]);
  };

  return (
    <Dialog width="large">
      <DialogHeader title="Add node" />
      <DialogContent>
        <Group spacing={16}>
          <Group spacing={4} padding={{horizontal: 20, vertical: 12}}>
            <label htmlFor="entityType">
              <Group>
                <BodyAlt>Stripe entity name</BodyAlt>
                <Body>
                  The name of the Stripe entity (e.g. PaymentIntent, Customer,
                  Subscription, etc).
                </Body>
              </Group>
            </label>
            <Group direction="horizontal" spacing={12}>
              <TextInput
                id="entityType"
                onChange={setType}
                value={type}
                onBlur={(evt) => {
                  // $FlowFixMe
                  setId(generateId(evt.target.value));
                }}
              />
            </Group>
          </Group>
          <Box
            divider={{color: 'light', width: 1, side: 'vertical'}}
            padding={{vertical: 12, horizontal: 20}}
          >
            <Group spacing={12} margin={{vertical: 12}}>
              <Group>
                <BodyAlt>Sample entity attributes</BodyAlt>
                <Body>
                  Add sample attributes to model a certain configuration, e.g.
                  adding a sample ID and amount to a PaymentIntent.
                </Body>
              </Group>
              {attributes.length > 0 ? (
                <Box divider={{color: 'light', width: 1, side: 'all'}}>
                  <PropertyList
                    divider
                    layout="inline"
                    size="medium"
                    title="Attributes"
                  >
                    {attributes.map((a) => (
                      <PropertyListItem
                        key={a.name}
                        label={a.name}
                        definition={a.value}
                        status={a.hasHandle ? 'attentionSoon' : undefined}
                      />
                    ))}
                  </PropertyList>
                </Box>
              ) : (
                ''
              )}
              <Group direction="horizontal" justifyContent="space-between">
                <Box>
                  <Group spacing={12}>
                    <label htmlFor="entityAttrName">
                      <BodyAlt>Name</BodyAlt>
                    </label>
                    <TextInput
                      id="entityAttrName"
                      onChange={setName}
                      value={name}
                    />
                  </Group>
                </Box>
                <Box>
                  <Group spacing={12}>
                    <label htmlFor="entityAttrValue">
                      <BodyAlt>Value</BodyAlt>
                    </label>
                    <TextInput
                      id="entityAttrValue"
                      onChange={setValue}
                      value={value}
                    />
                  </Group>
                </Box>
              </Group>
              <Group alignItems="center" direction="horizontal" spacing={12}>
                <Checkbox
                  checked={hasHandle}
                  onChange={(evt) => {
                    setHasHandle(!hasHandle);
                  }}
                  label="Has handle?"
                />
                <InfoTooltip content="Check this checkbox if you want to drag an edge from this sample attribute row to another node." />
              </Group>
              <Box>
                <Button
                  label="Add attribute"
                  onClick={() => {
                    const newAttributes = [
                      ...attributes,
                      {name, value, hasHandle},
                    ];
                    setAttributes(newAttributes);
                    setName('');
                    setValue('');
                    setHasHandle(false);
                  }}
                />
              </Box>
            </Group>
          </Box>
          <Box padding={{horizontal: 20}}>
            <SetNodeId id={id} setId={setId} />
          </Box>
        </Group>
      </DialogContent>
      <DialogFooter end={<Button label="Add" onClick={add} />} />
    </Dialog>
  );
};
